import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { 
  clearDrillDown, 
  drillUp, 
  navigateToLevel,
  navigateDrillDown,
  selectCurrentPath,
  selectDrillDownData,
  selectDrillDownLoading
} from '../../store/drilldownSlice';
import { KpiType, DrillDownBreakdown } from '../../types/drilldown';
import { DrillDownBreadcrumbs } from './DrillDownBreadcrumbs';
import { DrillDownBreakdownGrid } from './DrillDownBreakdownGrid';
import { DrillDownSummary } from './DrillDownSummary';
import { DrillDownIndividualsTable } from './DrillDownIndividualsTable';
import { Colors, Spacing, FontSizes, BorderRadius, Shadows } from '../../constants/theme';

interface DrillDownModalProps {
  isOpen: boolean;
  kpi: KpiType;
  onClose: () => void;
}

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div<{ isOpen: boolean }>`
  background: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.modal};
  width: 90vw;
  max-width: 1200px;
  max-height: 90vh;
  overflow: hidden;
  transform: ${props => props.isOpen ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(-20px)'};
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.xl}px;
  border-bottom: 1px solid ${Colors.border};
  background: ${Colors.backgroundLight};
`;

const ModalTitle = styled.h2`
  font-size: ${FontSizes.xxl}px;
  font-weight: 700;
  color: ${Colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
`;

const KpiIcon = styled.div<{ kpi: KpiType }>`
  width: 32px;
  height: 32px;
  border-radius: ${BorderRadius.sm}px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${FontSizes.lg}px;
  background: ${props => {
    switch (props.kpi) {
      case 'totalEmployees': return Colors.dataViz.primary;
      case 'attritionRisk': return Colors.dataViz.series5;
      case 'engagementScore': return Colors.dataViz.series2;
      case 'leadershipReady': return Colors.dataViz.series6;
      case 'timeToFill': return Colors.dataViz.series3;
      case 'diversityScore': return Colors.dataViz.series7;
      default: return Colors.secondary;
    }
  }};
  color: white;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: ${FontSizes.xxl}px;
  color: ${Colors.textTertiary};
  cursor: pointer;
  padding: ${Spacing.sm}px;
  border-radius: ${BorderRadius.sm}px;
  transition: all 0.2s ease;

  &:hover {
    background: ${Colors.surfaceHover};
    color: ${Colors.textPrimary};
  }
`;

const ModalContent = styled.div`
  padding: ${Spacing.xl}px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const ActionBar = styled.div`
  display: flex;
  gap: ${Spacing.sm}px;
  margin-bottom: ${Spacing.lg}px;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  border-radius: ${BorderRadius.sm}px;
  border: 1px solid ${props => props.variant === 'primary' ? Colors.primary : Colors.border};
  background: ${props => props.variant === 'primary' ? Colors.primary : Colors.surface};
  color: ${props => props.variant === 'primary' ? Colors.textOnPrimary : Colors.textSecondary};
  font-size: ${FontSizes.sm}px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.variant === 'primary' ? Colors.primaryDark : Colors.surfaceHover};
    color: ${props => props.variant === 'primary' ? Colors.textOnPrimary : Colors.textPrimary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${Spacing.xxxl}px;
  color: ${Colors.textTertiary};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.xxxxl}px;
  gap: ${Spacing.lg}px;
  min-height: 300px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${Colors.borderLight};
  border-top: 3px solid ${Colors.secondary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: ${FontSizes.md}px;
  color: ${Colors.textSecondary};
  font-weight: 500;
`;

const LoadingSubtext = styled.div`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textTertiary};
  text-align: center;
  max-width: 300px;
  line-height: 1.4;
`;

const getKpiTitle = (kpi: KpiType): string => {
  switch (kpi) {
    case 'totalEmployees': return 'Total Employees';
    case 'attritionRisk': return 'Attrition Risk';
    case 'engagementScore': return 'Engagement Score';
    case 'leadershipReady': return 'Leadership Ready';
    case 'timeToFill': return 'Time to Fill';
    case 'diversityScore': return 'Diversity Score';
    default: return 'KPI Analysis';
  }
};

const getKpiIcon = (kpi: KpiType): string => {
  switch (kpi) {
    case 'totalEmployees': return '👥';
    case 'attritionRisk': return '⚠️';
    case 'engagementScore': return '💡';
    case 'leadershipReady': return '🎯';
    case 'timeToFill': return '⏱️';
    case 'diversityScore': return '🌈';
    default: return '📊';
  }
};

export const DrillDownModal: React.FC<DrillDownModalProps> = ({
  isOpen,
  kpi,
  onClose,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const currentPath = useSelector(selectCurrentPath);
  const drillDownData = useSelector(selectDrillDownData);
  const loading = useSelector(selectDrillDownLoading);
  const currentUser = useSelector((state: RootState) => state.user);

  useEffect(() => {
    if (isOpen && !currentPath) {
      console.log('DrillDown Modal: Initializing drill-down for KPI:', kpi);
      // Initialize drill-down for the KPI at department level for better demonstration
      dispatch(navigateDrillDown({
        kpi,
        level: 'department',
        userContext: {
          userId: currentUser.userName || 'anonymous',
          role: currentUser.currentRole || 'HR_LEADER',
          permissions: ['viewIndividualData', 'viewEmployeeNames', 'viewSensitiveData', 'exportData'], // Add basic permissions
          department: currentUser.department
        }
      }));
    }
  }, [isOpen, kpi, currentPath, dispatch, currentUser]);

  // Debug logging
  useEffect(() => {
    console.log('DrillDown Modal State:', {
      isOpen,
      currentPath,
      drillDownData,
      loading,
      dataBreakdowns: drillDownData?.breakdowns?.length
    });
  }, [isOpen, currentPath, drillDownData, loading]);

  const handleClose = () => {
    dispatch(clearDrillDown());
    onClose();
  };

  const handleDrillUp = () => {
    dispatch(drillUp());
  };

  const handleNavigateToLevel = (breadcrumbIndex: number) => {
    dispatch(navigateToLevel({ breadcrumbIndex }));
  };

  const handleDrillDown = (breakdown: DrillDownBreakdown) => {
    if (!breakdown.canDrillDown || !breakdown.nextLevel || !currentPath) return;

    console.log('🔽 Drilling down from', currentPath.level, 'to', breakdown.nextLevel, 'with filter:', breakdown.category);

    // Create new filter for the selected breakdown
    const newFilter = {
      dimension: `${currentPath.level}_${breakdown.category.toLowerCase().replace(/\s+/g, '_')}`,
      value: breakdown.category,
      displayName: breakdown.category
    };

    // Add breadcrumb for current selection
    const newBreadcrumb = {
      level: breakdown.nextLevel,
      label: `${breakdown.category} (${breakdown.nextLevel})`,
      value: breakdown.category,
      filters: [...currentPath.filters, newFilter]
    };

    dispatch(navigateDrillDown({
      kpi: currentPath.kpi,
      level: breakdown.nextLevel,
      selectedValue: breakdown.category,
      filters: [...currentPath.filters, newFilter],
      userContext: {
        userId: currentUser.userName || 'anonymous',
        role: currentUser.currentRole || 'HR_LEADER',
        permissions: ['viewIndividualData', 'viewEmployeeNames', 'viewSensitiveData', 'exportData'],
        department: currentUser.department
      }
    }));
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleClose}>
      <ModalContainer isOpen={isOpen} onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <KpiIcon kpi={kpi}>{getKpiIcon(kpi)}</KpiIcon>
            {getKpiTitle(kpi)} Analysis
          </ModalTitle>
          <CloseButton onClick={handleClose} aria-label="Close drill-down">
            ×
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          {loading && (
            <LoadingContainer>
              <LoadingSpinner />
              <LoadingText>Analyzing {getKpiTitle(kpi)} Data</LoadingText>
              <LoadingSubtext>
                Processing departmental breakdowns and generating insights...
                <br />This may take a few seconds.
              </LoadingSubtext>
            </LoadingContainer>
          )}

          {!loading && currentPath && (
            <>
              <ActionBar>
                <ActionButton
                  onClick={handleDrillUp}
                  disabled={currentPath.level === 'department'}
                >
                  ← Drill Up
                </ActionButton>
                <ActionButton variant="secondary">
                  📊 Export Data
                </ActionButton>
                <ActionButton variant="secondary">
                  📈 View Trends
                </ActionButton>
              </ActionBar>

              <DrillDownBreadcrumbs
                breadcrumbs={currentPath.breadcrumbs}
                onNavigateToLevel={handleNavigateToLevel}
              />

              {drillDownData && (
                <>
                  <DrillDownSummary summary={drillDownData.summary} />
                  <DrillDownBreakdownGrid
                    breakdowns={drillDownData.breakdowns}
                    onDrillDown={handleDrillDown}
                    loading={false}
                  />
                  {drillDownData.individuals && drillDownData.individuals.length > 0 && (
                    <DrillDownIndividualsTable
                      individuals={drillDownData.individuals}
                      loading={false}
                    />
                  )}
                </>
              )}

              {!drillDownData && (
                <EmptyState>
                  No drill-down data available for this view.
                </EmptyState>
              )}
            </>
          )}
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
};